import { GridColDef } from '@mui/x-data-grid-premium';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import React, { useEffect } from 'react';
import { tableSettingsProps, useTableSettings } from '../tableSettings/useTableSettings';
import tableHelper from 'src/utils/tableHelper';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import { useGetReceivingOrderByWarehouse, useGetReceivingOrdersByWarehouse } from '../../api/requestConfigs/warehouse';

const receivingAdviceTypes: Record<string, string> = { '1': 'Purchase Order', '2': 'Transfer Order', '3': 'Return To Warehouse Order' };
const receivingAdviceTypeColumn: GridColDef = {
    field: 'ReceivingAdviceTypeId', headerName: 'Receiving Advice Type', minWidth: 220,
    renderCell: (params) => receivingAdviceTypes[String(params.value)] ?? String(params.value ?? ''),
};

export type ReceivingOrderRow = {
    ReceivingAdviceRowId: number | string;
    ReceivingAdviceId: number | string;
    WarehouseId: number | string;
    ProductId?: number | string | null;
    StorageItemId?: number | string | null;
    ReceivingAdviceTypeId?: number | string | null;
    ManufacturerProductId?: number | string | null;
    ProductName?: string | null;
    Sender?: string | null;
    SenderNote?: string | null;
    QuantityExpected?: number | null;
    QuantityReceive?: number | null;
    IsReceived?: boolean;
    DeliveryNoteRef?: string | null;
    [field: string]: unknown;
};

const emptyReceivingRows: ReceivingOrderRow[] = [];
const receivingRowIdPath = ['ReceivingAdviceRowId'];

export type ReceivingOrdersTableData = {
    rows: ReceivingOrderRow[];
    status: ReturnType<typeof useGetReceivingOrdersByWarehouse>['status'];
};

// Third tuple item exposes the source rows and status without a second
// API request. Existing callers that destructure just two items still work.
export const useReceivingOrdersTable = (warehouseId: number | string, refreshKey?: unknown, onSelect?: (row: ReceivingOrderRow) => void): [columns: GridColDef[], tableSettings: tableSettingsProps, data: ReceivingOrdersTableData] => {
    const { rows: responseRows, status } = useGetReceivingOrdersByWarehouse(warehouseId, refreshKey);
    const rows: ReceivingOrderRow[] = Array.isArray(responseRows) ? responseRows : emptyReceivingRows;

    const columns = React.useMemo<GridColDef[]>(() => [
      
        ...(onSelect ? [{
            field: 'select', headerName: '', width: 80, sortable: false, filterable: false, disableColumnMenu: true, disableExport: true,
            renderCell: (params) => React.createElement(CustomButton, {
                iconButton: true,
                size: 'small',
                icon: React.createElement(ArrowUpwardIcon),
                title: 'Load into form', tooltipText: 'Load this row into the receiving form.',
                onClick: (event: React.MouseEvent) => { event.stopPropagation(); onSelect(params.row as ReceivingOrderRow); },
            }),
        } as GridColDef] : []),
        { field: 'ReceivingAdviceRowId', headerName: 'Receiving Advice Row Id' },
        { field: 'ProductId', headerName: 'Product Id' },
        { field: 'ProductName', headerName: 'Product', flex: 1, minWidth: 200 },
        { field: 'QuantityExpected', headerName: 'Quantity Expected', type: 'number' },
        { field: 'ReceivedQty', headerName: 'Quantity Received', type: 'number' },
        { field: 'IsReceived', headerName: 'Received', type: 'boolean' },
        { field: 'StorageItemId', headerName: 'Storage Item Id' },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id' },
        { field: 'EANCode', headerName: 'Barcode(EAN)' },
        { field: 'CreateDate', headerName: 'Create Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
        { field: 'DeliveryNoteRef', headerName: 'Delivery Note Ref' },
        { field: 'ReceivingAdviceId', headerName: 'Receiving Advice Id' },
        { field: 'WarehouseId', headerName: 'Warehouse Id' },
        { field: 'ExternalDocumentId', headerName: 'External Document Id' },
        receivingAdviceTypeColumn,
        { field: 'Sender', headerName: 'Sender', flex: 1, minWidth: 160 },
        { field: 'SenderNote', headerName: 'Sender Note', flex: 1, minWidth: 160 },
        { field: 'ItemId', headerName: 'Item Id' },
    ], [onSelect]);

    const tableName = 'ReceivingOrdersTable';
    const tableSettings = useTableSettings({ columns, idPath: receivingRowIdPath, rows, tableName, status });

    useEffect(() => {
        tableSettings.setColumnVisibilityModel({ StorageItemId: false, WarehouseId: false, ReceivingAdviceTypeId: true, ItemId: false });
    }, []);
    tableSettings.loading=false;

    return [columns, tableSettings, { rows, status }];
};

export const useReceivingOrderMatchesTable = (rows: ReceivingOrderRow[], onSelect: (row: ReceivingOrderRow) => void, status: ReceivingOrdersTableData['status']): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = React.useMemo<GridColDef[]>(() => [
        // {
        //     field: 'select', headerName: 'Choose', width: 80, sortable: false, filterable: false, disableColumnMenu: true,
        //     renderCell: (params) => React.createElement(CustomButton, {
        //         iconButton: true,
        //         icon: React.createElement(OutboxIcon),
        //         tooltipText: `Choose receiving row ${params.row.ReceivingAdviceRowId}`,
        //         onClick: (event: React.MouseEvent) => { event.stopPropagation(); onSelect(params.row as ReceivingOrderRow); },
        //     }),
        // },
        { field: 'ReceivingAdviceId', headerName: 'Receiving Order ID', minWidth: 160 },
        { field: 'ReceivingAdviceRowId', headerName: 'Receiving Row ID', minWidth: 150 },
        receivingAdviceTypeColumn,
        { field: 'Sender', headerName: 'Sender', minWidth: 180, flex: 1 },
        { field: 'ProductId', headerName: 'Product ID', minWidth: 120 },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', minWidth: 200 },
        { field: 'ProductName', headerName: 'Product', minWidth: 180, flex: 1 },
        { field: 'QuantityExpected', headerName: 'Expected', type: 'number', minWidth: 100 },
        { field: 'QuantityReceive', headerName: 'Received Qty', type: 'number', minWidth: 120 },
        { field: 'IsReceived', headerName: 'Received', type: 'boolean', minWidth: 100 },
        { field: 'DeliveryNoteRef', headerName: 'Delivery Note Ref', minWidth: 160 },
        { field: 'SenderNote', headerName: 'Sender Note', minWidth: 180, flex: 1 },
    ], [onSelect]);

    const tableSettings = useTableSettings({ columns, idPath: receivingRowIdPath, rows, tableName: 'ReceivingOrderMatchesTable', status });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;

    useEffect(() => {
        const selection = tableSettings.rowSelectionModel ?? [];
        if (!Array.isArray(selection) || selection.length === 0) return;

        const selectedId = selection[0];
        const selectedRow = rows.find((row) => String(row.ReceivingAdviceRowId) === String(selectedId));
        if (!selectedRow) return;

        tableSettings.setRowSelectionModel?.([]);
        onSelect(selectedRow);
    }, [tableSettings.rowSelectionModel, rows, onSelect]);

    return [columns, tableSettings];
};

export const useReceivingOrderRowsTable = (receivingAdviceId: number | string): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const { rows: receivingAdvice, status }: any = useGetReceivingOrderByWarehouse(receivingAdviceId, 12);
    const rows = receivingAdvice?.ReceivingAdviceRows || [];
    const idPath = ['Id'];

    const columns: GridColDef[] = React.useMemo(() => [
        { field: 'Id', headerName: 'Row Id' },
        { field: 'ExternalDocumentRowId', headerName: 'External Document Row Id' },
        { field: 'ProductId', headerName: 'Product Id' },
        { field: 'ProductName', headerName: 'Product', flex: 1, minWidth: 220 },
        { field: 'Quantity', headerName: 'Quantity', type: 'number' },
        //IsReceived
        { field: 'IsReceived', headerName: 'Received', type: 'boolean' },
        { field: 'StorageValue', headerName: 'Storage Value', type: 'number' },
        { field: 'ETA', headerName: 'ETA', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
    ], []);

    const tableName = 'ReceivingOrderRowsTable';
    const tableSettings = useTableSettings({ columns, idPath, rows, tableName, status });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;
    tableSettings.hideFooter = true;

    return [columns, tableSettings];
};
